import { useState } from 'react'
import { TableShell } from '../../components/molecules/TableShell'
import { Alert } from '../../components/atoms/Alert'
import { Button } from '../../components/atoms/Button'
import { Spinner } from '../../components/atoms/Spinner'
import { Th, Td } from '../../components/molecules/Table'
import { Paginator } from '../../components/molecules/Paginator'
import { ManagePurchaseFormModal } from './ManagePurchaseFormModal'
import { useManagePurchases, type ManagePurchaseDto } from '../../../viewmodels/useManagePurchases'
import { useAuth } from '../../../context/AuthContext'
import { SCREENCODES } from '../../../constants/screen.constants'

/**
 * /manage/purchases — bare per-buy ledger (no supplier / raw_material /
 * product link). Use for ad-hoc operational purchases that don't fit
 * the manufacturing or warehouse pipelines. Bill upload (file browse)
 * lives in the edit modal.
 */
export function ManagePurchasesPage(): JSX.Element {
  const vm = useManagePurchases()
  const { can } = useAuth()
  const [modalMode, setModalMode] = useState<'closed' | 'create' | 'edit'>('closed')
  const [editTarget, setEditTarget] = useState<ManagePurchaseDto | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const openCreate = (): void => {
    setEditTarget(null)
    setModalMode('create')
  }
  const openEdit = (row: ManagePurchaseDto): void => {
    setEditTarget(row)
    setModalMode('edit')
  }
  const closeModal = (): void => setModalMode('closed')

  const handleDelete = async (row: ManagePurchaseDto): Promise<void> => {
    if (typeof window !== 'undefined' && !window.confirm('Delete this purchase?')) return
    setDeletingId(row.id)
    setDeleteError(null)
    const res = await vm.remove(row.id)
    setDeletingId(null)
    if (!res.ok) setDeleteError(res.message)
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between gap-4">
        <h1 className="font-heading text-h2 text-charcoal">Purchases</h1>
        {can(SCREENCODES.MANAGEPURCHASES, 'add') ? (
          <Button onClick={openCreate}>Add purchase</Button>
        ) : null}
      </header>

      {deleteError !== null ? <Alert tone="error">{deleteError}</Alert> : null}
      {vm.error !== null ? (
        <Alert tone="error">
          {vm.error}{' '}
          <button
            type="button"
            className="underline underline-offset-2 hover:text-charcoal"
            onClick={() => void vm.reload()}
          >
            Retry
          </button>
        </Alert>
      ) : null}

      {vm.isLoading ? (
        <div className="flex items-center justify-center py-12"><Spinner /></div>
      ) : vm.items.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          <Table
            items={vm.items}
            onEdit={openEdit}
            onDelete={(row) => void handleDelete(row)}
            deletingId={deletingId}
          />
          <Paginator
            page={vm.page}
            pageSize={vm.pageSize}
            total={vm.total}
            onPageChange={vm.setPage}
            disabled={vm.isLoading}
          />
        </>
      )}

      <ManagePurchaseFormModal
        open={modalMode !== 'closed'}
        mode={modalMode === 'edit' ? 'edit' : 'create'}
        initial={editTarget}
        currentGstCgstBps={vm.currentGstCgstBps}
        currentGstSgstBps={vm.currentGstSgstBps}
        units={vm.units}
        suppliers={vm.suppliers}
        onClose={closeModal}
        onSubmit={async (input) => {
          if (modalMode === 'edit' && editTarget !== null) {
            return vm.update(editTarget.id, input)
          }
          return vm.create(input as Parameters<typeof vm.create>[0])
        }}
        getBill={vm.getBill}
        uploadBill={vm.uploadBill}
        deleteBill={vm.deleteBill}
      />
    </div>
  )
}

function EmptyState(): JSX.Element {
  return (
    <div className="text-center py-16 border border-border rounded-md bg-parchment/40">
      <p className="font-heading text-h3 text-charcoal">No purchases yet</p>
      <p className="font-body text-body-sm text-text-secondary mt-2">
        Use <span className="font-medium">Add purchase</span> at the top right to record your first buy.
      </p>
    </div>
  )
}

function formatPaisa(p: number): string {
  return `₹${(p / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}
function formatQty(millis: number): string {
  return (millis / 1000).toLocaleString('en-IN', { maximumFractionDigits: 3 })
}
function formatDate(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: '2-digit'
  })
}

function Table({
  items,
  onEdit,
  onDelete,
  deletingId
}: {
  readonly items: ManagePurchaseDto[]
  readonly onEdit: (row: ManagePurchaseDto) => void
  readonly onDelete: (row: ManagePurchaseDto) => void
  readonly deletingId: string | null
}): JSX.Element {
  const { can } = useAuth()
  const canEdit = can(SCREENCODES.MANAGEPURCHASES, 'edit')
  const canDelete = can(SCREENCODES.MANAGEPURCHASES, 'delete')
  return (
    <TableShell>
      <table className="w-full font-body text-body-sm">
        <thead className="bg-parchment/50 text-text-secondary">
          <tr>
            <Th>Invoice #</Th>
            <Th>Supplier</Th>
            <Th>Purchase date</Th>
            <Th>Billed date</Th>
            <Th className="text-right">Qty</Th>
            <Th className="text-right">Total paid</Th>
            <Th className="text-right">CGST</Th>
            <Th className="text-right">SGST</Th>
            <Th className="text-right">Total GST</Th>
            <Th>Bill</Th>
            <Th>Notes</Th>
            <Th className="text-right">Actions</Th>
          </tr>
        </thead>
        <tbody>
          {items.map((row) => (
            <tr key={row.id} className="border-t border-border">
              <Td className="font-mono whitespace-nowrap">
                {row.invoice_no ?? <span className="text-text-secondary">—</span>}
              </Td>
              <Td className="whitespace-nowrap">
                {row.supplier_name ?? <span className="text-text-secondary">—</span>}
              </Td>
              <Td className="whitespace-nowrap">{formatDate(row.purchase_date)}</Td>
              <Td className="whitespace-nowrap">{formatDate(row.billed_date)}</Td>
              <Td className="text-right whitespace-nowrap">
                {formatQty(row.quantity_millis)}{' '}
                <span className="font-mono text-body-xs text-text-secondary">{row.unit_label}</span>
              </Td>
              <Td className="text-right whitespace-nowrap">{formatPaisa(row.total_paid_paisa)}</Td>
              <Td className="text-right whitespace-nowrap">{formatPaisa(row.cgst_amount_paisa)}</Td>
              <Td className="text-right whitespace-nowrap">{formatPaisa(row.sgst_amount_paisa)}</Td>
              <Td className="text-right whitespace-nowrap font-medium">{formatPaisa(row.cgst_amount_paisa + row.sgst_amount_paisa)}</Td>
              <Td>
                {row.bill_image_storage_key === null ? (
                  <span className="text-text-secondary">—</span>
                ) : (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-bamboo/20 text-charcoal font-medium text-body-xs">
                    attached
                  </span>
                )}
              </Td>
              <Td className="max-w-xs truncate" title={row.notes ?? ''}>
                {row.notes === null || row.notes === '' ? <span className="text-text-secondary">—</span> : row.notes}
              </Td>
              <Td className="text-right whitespace-nowrap">
                {canEdit ? (
                  <>
                    <Button size="sm" variant="secondary" onClick={() => onEdit(row)}>Edit</Button>{' '}
                  </>
                ) : null}
                {canDelete ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onDelete(row)}
                    disabled={deletingId === row.id}
                  >
                    {deletingId === row.id ? 'Deleting…' : 'Delete'}
                  </Button>
                ) : null}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableShell>
  )
}
